import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ISbStoryData } from '@storyblok/js';
import { DynamicModule, InputsType } from 'ng-dynamic-component';
import { MarkdownModule } from 'ngx-markdown';
import { combineLatest, from, map, Observable, switchMap, tap } from 'rxjs';

import { CarouselComponent } from '@valk-nx/components/ui-carousel/src/lib/carousel';
import {
	LANGUAGE,
	Language,
	LinkTarget,
	negativeHeader$,
} from '@valk-nx/core/lib/core';
import { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { CardComponent } from '@valk-nx/storyblok/components/card/src/lib/card';
import { HotelByDealListComponent } from '@valk-nx/storyblok/components/hotel-by-deal-list/src/lib/hotel-by-deal-list';
import { ReferenceComponent } from '@valk-nx/storyblok/components/reference/src/lib/reference.component';
import { SectionComponent } from '@valk-nx/storyblok/components/section/src/lib/section';
import { SheetComponent } from '@valk-nx/storyblok/components/sheet/src/lib/sheet';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { removeTrailingSlash } from '@valk-nx/storyblok-helpers/src/lib/general/sitemap.helper';
import { DealData } from '@valk-nx/storyblok-services/src/lib/services/content.interface';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import {
	CarouselItem,
	HotelByDealCardInterface,
	ISbComponentType,
	ISbStoryBlok,
	TranslatedSlug,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.types';

const Components = {
	reference: ReferenceComponent,
	section: SectionComponent,
};

@Component({
	selector: 'sb-deal-detail',
	templateUrl: './deal-detail.component.html',
	imports: [
		CardComponent,
		CarouselComponent,
		CommonModule,
		DynamicModule,
		HotelByDealListComponent,
		MarkdownModule,
		SheetComponent,
		TranslatePipe,
	],
})
export class DealDetailComponent extends StoryblokRootDirective {
	private readonly contentService = inject(ContentService);
	private readonly sbService = inject(StoryblokService);
	private readonly metadataService = inject(MetadataService);
	private readonly router = inject(Router);
	private readonly translate = inject(TranslateService);
	private readonly rootLanguage = inject<Language>(LANGUAGE);

	@Input() content: ISbStoryBlok[] = [];

	components = Components;
	currentUrl = '';
	deal$: Observable<DealData>;
	images: CarouselItem[] = [];
	hotels: HotelByDealCardInterface[] = [];

	get contentInputs(): (InputsType & { component: string })[] {
		return this.content?.map((blok) => ({
			...blok,
			component: blok['component'] ?? '',
		}));
	}

	constructor() {
		super();

		this.deal$ = this.sbService.translatedSlugs$.pipe(
			takeUntilDestroyed(),
			map((translatedSlugs) => this.extractPathInfo(translatedSlugs)),
			switchMap(({ path, language }) =>
				combineLatest([
					from(
						this.sbService.getStoriesByQuery(
							{
								starts_with: path,
								excluding_slugs: path,
							},
							language,
						),
					),
					this.contentService.getDeal(
						path.split('/').pop(),
						language,
					),
				]),
			),
			tap(([storiesResponse, dealResponse]) =>
				this.handleSideEffects(
					storiesResponse.data.stories,
					dealResponse.data,
				),
			),
			map(([_, dealResponse]) => dealResponse.data),
		);
	}

	private extractPathInfo(translatedSlugs: TranslatedSlug[]): {
		path: string;
		language: Language;
	} {
		const urlTree = this.router.parseUrl(this.router.url);
		this.currentUrl = removeTrailingSlash(
			urlTree.root.children['primary']?.segments
				.map((s) => s.path)
				.join('/') ?? '',
		);

		const selectedSlug =
			translatedSlugs.find((x) => x.selected) ?? translatedSlugs[0];
		const path =
			selectedSlug.lang.toLowerCase() !== this.rootLanguage
				? selectedSlug.path.substring(3)
				: selectedSlug.path;

		return {
			path: removeTrailingSlash(path),
			language: selectedSlug.lang.toLowerCase() as Language,
		};
	}

	private handleSideEffects(
		storyblokHotels: ISbStoryData<
			ISbComponentType<string> & Record<string, unknown>
		>[],
		deal: DealData,
	) {
		this.metadataService.updateMetaDataBatch(
			deal,
			this.robotsNoFollow,
			this.robotsNoIndex,
		);

		this.images =
			deal.images?.length > 0
				? deal.images.map((image) => ({
						image: {
							src: image.src,
							alt: image.alt,
						},
						heading: deal.title,
						subHeading: this.translate.instant('global.at-valk'),
						sticker: null,
						button: null,
						link: null,
					}))
				: [];

		if (this.images.length > 0) {
			negativeHeader$.next(true);
		}

		this.hotels = this.buildHotelCards(storyblokHotels, deal);
	}

	private buildHotelCards(
		storyblokHotels: ISbStoryData<
			ISbComponentType<string> & Record<string, unknown>
		>[],
		deal: DealData,
	): HotelByDealCardInterface[] {
		if (!deal.hotels?.length) return [];

		return deal.hotels
			.filter((availableHotel) =>
				storyblokHotels.some(
					(storyblokHotel) =>
						storyblokHotel.full_slug.split('/').pop() ===
						availableHotel.slug,
				),
			)
			.map((availableHotel) => ({
				component: 'hotelByDealCard',
				ctaUrl: {
					cached_url: `${this.currentUrl}/${availableHotel.slug}`,
					target: '_self' as LinkTarget,
					url: '',
				},
				currency: availableHotel.price?.currency ?? 'EUR',
				hotelName: availableHotel.name,
				hotelSlug: availableHotel.slug,
				image: this.buildHotelImages(availableHotel.images),
				price:
					availableHotel.isAvailable === false
						? '0'
						: (availableHotel.price?.amount?.toString() ?? ''),
				numberOfRatings:
					availableHotel.rating?.numberOfRatings?.toString() ?? '',
				numberOfStars: availableHotel.rating?.stars?.toString() ?? '',
				rating: availableHotel.rating?.score?.toString() ?? '',
				ribbonColor: 'yellow',
				ribbonText: deal.ribbonText,
				tags: this.buildTextComponents(deal.tags ?? []),
				validFrom: deal.visible.from,
				validUntil: deal.visible.to,
				usps: this.buildTextComponents(availableHotel.usps ?? []),
			}));
	}

	private buildHotelImages(images: PhotoInterface[]) {
		if (!images || images.length === 0) {
			return undefined;
		}

		return images.map((image) => ({
			_editable: null,
			_uid: null,
			component: 'cardImage',
			bynder: [
				{
					databaseId: null,
					description: image.alt,
					files: {
						mini: {
							url: image.src,
							width: 80,
							height: 80,
							fileSize: null,
						},
						webImage: {
							url: image.src,
							width: null,
							height: null,
							fileSize: null,
						},
						thumbnail: {
							url: image.src,
							width: 167,
							height: 250,
							fileSize: null,
						},
						transformBaseUrl: {
							url: image.src,
							width: null,
							height: null,
							fileSize: null,
						},
					},
					id: null,
					name: image.alt,
				},
			],
			imageAlt: image.alt,
		}));
	}

	private buildTextComponents(texts?: string[]) {
		if (!texts) {
			return undefined;
		}

		return texts.map((text) => ({
			component: 'text',
			text,
			_editable: null,
			_uid: null,
		}));
	}
}
